import {Brick, Query, BrickContext, ErrorFlow, Predicate, Transaction, registerBrick, QueryResult} from 'olympe';
import Aspect from "../../../models/Aspect";
import System from "../../../models/System";
import Component from "../../../models/Component";
import WeightingFactor from "../../../models/WeightingFactor";
import AHPValue from "../../../models/AHPValue";

/** Analytic hierarchy process. Values are calculated once upon importing modules and are used to build graphs
*/
export default class ComputeAHPValues extends Brick {

    /** 
     * @protected
     * @param {BrickContext} $
     * @param {System} system
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setErrorFlow
     */
    async update($, [system], [forwardEvent, setErrorFlow]) {
        const aspects = Query.instancesOf(Aspect).executeFromCache();

        // Only consider last level of components, the ones having inspections, other levels are "folders" and won't get any AHP Value.
        const componentsWithInspections = await system.follow(System.allComponentsRel)
            .andReturn()
            .follow(Component.inspectionsRel)
            .executeFromCache();

        const transaction = new Transaction();
        for (const aspect of aspects.values()) {
            // 1. Compute AHP Value for inspections of each component
            componentsWithInspections.forEach((component) => this.computeInspectionsAHPValues(transaction, component, aspect));

            // 2. Compute AHP Value for components having inspections
            this.computeComponentAHPValues(transaction, componentsWithInspections, aspect);
        }

        try {
            await transaction.executeAsLarge();
            forwardEvent();
        } catch(e) {
            setErrorFlow(ErrorFlow.create(`Error occurred while executing transaction to create AHP Values: ${e}`, 1));
        }
    }

    /**
     * @private
     * @param {Transaction} transaction
     * @param {number} value
     * @param {InspectionEntity} item
     * @param {Aspect} aspect
     */
    createAHPValue(transaction, value, item, aspect) {
        const AHPObject = transaction.create(AHPValue, new Map().set(AHPValue.valueProp, value));
        transaction.createRelation(AHPValue.itemRel, AHPObject, item);
        transaction.createRelation(AHPValue.aspectRel, AHPObject, aspect);
    }

    /**
     * @private
     * @param {!Transaction} transaction
     * @param {!Component} component
     * @param {!Aspect} aspect
     */
    computeInspectionsAHPValues(transaction, component, aspect) {
        // Get inspections of the current component
        const inspections = component.follow(Component.inspectionsRel).executeFromCache();
        const factors = inspections.map((inspection) => {
            // Extract weighting factors of the current component related to the current aspect
            return inspection.follow(WeightingFactor.itemRel.getInverse()).andReturn()
                .follow(WeightingFactor.targetRel).filter(Predicate.in(aspect))
                .executeFromCache()
                .getFirst()?.get(WeightingFactor.valueProp) ?? 0;
        });

        // Compute AHP Values for current aspect
        const AHPValues = this.computeAhpValuesFromFactors(factors);

        // Create the AHP Value for each inspection from computed factors
        inspections.forEach((inspection, index) => {
            this.createAHPValue(transaction, AHPValues[index], inspection, aspect);
        });
    }

    /** 
     * @private
     * @param {!Transaction} transaction
     * @param {!QueryResult<Component>} components
     * @param {!Aspect} aspect
     */
    computeComponentAHPValues(transaction, components, aspect) {
        // Extract influences linked to the current aspect
        const influences = aspect.follow(Aspect.influencesRel).executeFromCache();

        const factors = components.map((component) => {
            // Extract weighting factors of the current component related to the influence of the current aspect
            const weightingFactors = component.follow(WeightingFactor.itemRel.getInverse()).andReturn()
                .follow(WeightingFactor.targetRel).filter(Predicate.in(...influences.values()))
                .executeFromCache()
                .map((factor) => factor.get(WeightingFactor.valueProp) ?? 0);

            // Accumulate the factors of influences to a single final factor for the aspect
            const finalFactor = weightingFactors.reduce((total, factor) => total * factor, 1);
            return Math.pow(finalFactor, 1 / weightingFactors.length);
        });

        // Compute AHP Values for current aspect
        const AHPValues = this.computeAhpValuesFromFactors(factors);

        // Create the AHP Value for each component from computed factors
        components.forEach((component, index) => {
            this.createAHPValue(transaction, AHPValues[index], component, aspect);
        });
    }

    /**
     * @private
     * @param {number[]} factors
     * @return {number[]}
     */
    computeAhpValuesFromFactors(factors) {
        // Initialize matrix A
        const n = factors.length;
        const matA = Array(n).fill(0).map((_x) => Array(n).fill(0));

        // Fill matrix A with values
        // -- for f_i > f_j => a_ij = f_i - f_j + 1
        // -- for f_i <= f_j => a_ij = 1/(f_i - f_j + 1)
        for (let i = 0; i < n; i++){
            const f_i = factors[i];
            for (let j = 0; j < n; j++){
                const f_j = factors[j];

                if (f_i >= f_j) {
                    matA[i][j] = f_i - f_j + 1;
                } else {
                    matA[i][j] = 1 / (f_j - f_i + 1);
                }
            }
        }

        // Compute B = A**4 (matrix multiplication)
        const matA_sq = this.squareMatrix(matA, matA);
        const matB = this.squareMatrix(matA_sq, matA_sq);

        // Compute weights
        const weights = Array(n).fill(0);
        let totalWeight = 0;
        for (let i = 0; i < matB.length; i++) {
            const rowB_i = matB[i];
            const w_i = rowB_i.reduce((tot, v) => tot + v, 0);
            weights[i] = w_i;
            totalWeight += w_i;
        }

        return weights.map((w_i) => w_i / totalWeight);
    }

    /**
     * @private
     * @param {number[][]} mat1
     * @param {number[][]} mat2
     * @return {number[][]}
     */
    squareMatrix(mat1, mat2) {
        // Get size
        const n = Array.isArray(mat1) ? mat1.length : 0;

        // Check matrices size
        const notValid = (mat, size) => size <= 0 || !Array.isArray(mat) || mat.length !== size || !Array.isArray(mat[0]) || mat[0].length !== size
        if (n <= 0 || notValid(mat1, n) || notValid(mat2, n)) {
            throw new Error('Invalid matrices for square multiplication.');
        }

        // Perform multiplication
        const result = Array(n).fill(0).map((_x) => Array(n).fill(0));
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                for (let k = 0; k < n; k++) {
                    result[i][j] += mat1[i][k] * mat2[k][j];
                }
            }
        }

        return result;
    }
}

registerBrick('0189bbed325463f16cbc', ComputeAHPValues);